import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { LanguageType } from 'common/constants';
import type { ADMIN_PANEL_TABS_KEYS } from 'components/Admin/constants';

type AppMessage = {
  type: 'error' | 'info' | 'success' | 'warning';
  message: string;
};

export type Translation = {
  id: string;
  en: string;
  translationKey: string;
  fr?: string;
};
type InitialState = {
  appMessages: AppMessage | null;
  pageContents: Array<Translation>;
  translations: Array<Translation>;
  temporaryLanguage: LanguageType;
  translationsIsPending: boolean;
  supportEmails: Array<string>;
  supportEmailsIsPending: boolean;
  translationsCount: number;
  adminPanelTabsPrevTab: ADMIN_PANEL_TABS_KEYS | null;
  isExportPending: boolean;
  appIsPending: boolean;
  paymentButtonIsPending: boolean;
};

export interface ContactRequestBody {
  firstName: string;
  lastName: string;
  senderEmail: string;
  recipientEmail: string;
  subject: string;
  message: string;
}
const initialState: InitialState = {
  appMessages: null,
  pageContents: [],
  translations: [],
  translationsCount: 0,
  temporaryLanguage: LanguageType.FR,
  translationsIsPending: false,
  supportEmails: [],
  supportEmailsIsPending: false,
  adminPanelTabsPrevTab: null,
  isExportPending: false,
  appIsPending: false,
  paymentButtonIsPending: false,
};

export const generalSlice = createSlice({
  name: 'general',
  initialState,
  reducers: {
    setPaymentButtonIsPending: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        paymentButtonIsPending: action.payload,
      };
    },
    setAppIsLoading: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        appIsPending: action.payload,
      };
    },
    setExportPendingStatus: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isExportPending: action.payload,
      };
    },
    setAdminPanelTabsPrevTab: (
      state,
      action: PayloadAction<null | ADMIN_PANEL_TABS_KEYS>,
    ) => {
      return {
        ...state,
        adminPanelTabsPrevTab: action.payload,
      };
    },
    setAllTranslations: (state, action: PayloadAction<Array<Translation>>) => {
      return {
        ...state,
        translations: action.payload,
      };
    },
    setTranslationsCount: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        translationsCount: action.payload,
      };
    },
    editTranslation: (state, action: PayloadAction<Translation>) => {
      return {
        ...state,
        pageContents: state.pageContents.map((item) =>
          item.id === action.payload.id ? action.payload : item,
        ),
        translations: state.translations.map((item) =>
          item.id === action.payload.id ? action.payload : item,
        ),
      };
    },
    setSupportEmailsPendingStatus: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        supportEmailsIsPending: action.payload,
      };
    },
    setSupportEmails: (state, action: PayloadAction<Array<string>>) => {
      return {
        ...state,
        supportEmails: action.payload,
      };
    },
    setTemporaryLanguage: (state, action: PayloadAction<LanguageType>) => {
      return {
        ...state,
        temporaryLanguage: action.payload,
      };
    },
    setTranslationsPendingStatus: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        translationsIsPending: action.payload,
      };
    },
    removeTranslation: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        translationsCount: state.translationsCount - 1,
        pageContents: state.pageContents.filter(
          (item) => item.id !== action.payload,
        ),
        translations: state.translations.filter(
          (item) => item.id !== action.payload,
        ),
      };
    },
    setUpdatedTranslation: (state, action: PayloadAction<Translation>) => {
      return {
        ...state,
        pageContents: state.pageContents.map((item) =>
          item.id === action.payload.id ? action.payload : item,
        ),
        translations: state.translations.map((item) =>
          item.id === action.payload.id ? action.payload : item,
        ),
      };
    },
    setNewTranslation: (state, action: PayloadAction<Translation>) => {
      return {
        ...state,
        translationsCount: state.translationsCount + 1,
        pageContents: state.pageContents.concat(action.payload),
        translations: state.translations.concat(action.payload),
      };
    },
    setPageContents: (state, action: PayloadAction<Array<Translation>>) => {
      return {
        ...state,
        pageContents: action.payload,
      };
    },
    setAppMessage: (state, action: PayloadAction<AppMessage | null>) => {
      return {
        ...state,
        appMessages: action.payload,
      };
    },
  },
});
